import axios from 'axios';

const api = axios.create({
    baseURL: `https://${process.env.REACT_APP_HOST}:${process.env.REACT_APP_PORT}/api/`,
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 intercept any error responses from the api
 and check if the token is no longer valid.
 ie. Token has expired
 logout the user if the token has expired
 **/

api.interceptors.response.use(
    (response) => response,
    (error) => {

        if (error.message === "Network Error") {
            error.message = "Sorry there was a problem on our end, please refresh the page and try again or contact techsupport@psychpress.com.au"
        }

        if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            switch (error.response.status) {
                case 400:
                    error.message = error.response.data || 'Sorry, your request was not valid. Please double check and try again.';
                    break;
                case 401:
                    setSessionToken(null);
                    window.location.href = "/session-expired";
                    break;
                case 403:
                    error.message = error.response.data || 'Sorry, you do not have sufficient permissions to access this feature.';
                    break;
                case 404:
                    window.location.href = "/not-found";
                    break;
                default:
                    window.location.href = "/something-has-gone-wrong";
                    break;
            }
        } else {
            window.location.href = "/something-has-gone-wrong";
        }
        return Promise.reject(error);
    }
);

export const setSessionToken = (token) => {
    if (token) {
        api.defaults.headers.common['X-SESSION-ID'] = token;
        sessionStorage.setItem('token', token);
    } else {
        delete api.defaults.headers.common['X-SESSION-ID'];
        sessionStorage.removeItem('token');
    }
};

export const login = async (username, password, mfa) => {
    return await api.post('authentication/login', {
        username: username,
        password: password
    }, {
        headers: { 'X-MFA-CODE': mfa }
    })
};

export const sendMfa = async (identifier, sendingMethod) => {
    await api.post(`userdata/mfa/send?userIdentifier=${identifier}&method=${sendingMethod}`);
};

export const logout = async () => {
    await api.post('authentication/logout');
    setSessionToken(null);
};

export const register = async (mobileNumber, email, password, firstname, lastname, organisation) => {
    await api.post('registration/register', {
        username: email,
        password: password,
        mobileNumber: mobileNumber,
        emailAddress: email,
        firstName: firstname,
        lastName: lastname,
        organisation: organisation
    });
};

export const addUser = async (email, password, firstname, lastname, userGroupSourceUser) => {
    await api.post('userdata/create', {
        username: email,
        password: password,
        emailAddress: email,
        firstName: firstname,
        lastName: lastname,
        userGroupSourceUser: userGroupSourceUser
    });
};

export const editUser = async (mobileNumber, emailAddress, firstName, lastName, userIdentifier) => {
    await api.put('userdata/edit', {
        userIdentifier: userIdentifier,
        firstName: firstName,
        lastName: lastName,
        mobileNumber: mobileNumber,
        emailAddress: emailAddress,
    });
};

export const editUserPassword = async (userIdentifier, password) => {
    await api.put('userdata/password/edit', {
        userIdentifier: userIdentifier,
        password: password
    });
};

export const getOrganisationUsers = async () => {
    return (await api.get('organisation/user')).data;
}

export const getOrganisationApplications = async () => {
    return (await api.get('organisation/application')).data;
}

export const getUserDetails = async (userIdentifier) => {
    return (await api.get(`userdata/details/${userIdentifier}`)).data;
}

export const getOwnDetails = async () => {
    return (await api.get('account/details')).data;
}

export const forgetPassword = async (email) => {
    await api.post('account/password/forget', { email })
}

export const resetPassword = async (password, token) => {
    await api.post('account/password/reset', { password, token })
}

export const getApplications = async () => {
    return (await api.get('userdata/application')).data;
}

export const getReports = async () => {
    return (await api.get('document')).data;
}

export const downloadReport = async (documentIdentifier) => {
    return (await api.get(`document/${documentIdentifier}/latest`)).data;
}

export const downloadReportByVersion = async (documentIdentifier, versionNumber) => {
    return (await api.get(`document/${documentIdentifier}/${versionNumber}`)).data;
}

export const downloadSubsequentFile = async (documentIdentifier, subsequentFileIdentifier) => {
    return (await api.get(`document/${documentIdentifier}/subsequentFile/${subsequentFileIdentifier}`)).data;
}

export const getOneTimeUrl = async (application) => {
    return (await api.get(`application/launchurl/${application}`)).data;
}

export const getUserPermissions = async () => {
    return (await api.get('permission')).data;
}

export const getAllUserGroups = async () => {
    return (await api.get('organisation/usergroup')).data;
}

export const addUserGroup = async (name) => {
    await api.post('usergroup', {
        name: name
    });
};

export const assignUsersToUserGroup = async (userGroupName, users) => {
    await api.post('user/assign/usergroup', {
        target: userGroupName,
        toAssign: users
    });
};

export const assignPermissionsToUserGroup = async (userGroupName, permissions) => {
    await api.post('permission/assign/usergroup', {
        target: userGroupName,
        toAssign: permissions
    });
};

export const assignApplicationsToUserGroup = async (userGroupName, applications) => {
    await api.post('application/assign/usergroup', {
        target: userGroupName,
        toAssign: applications
    });
};

export const assignUserGroupsToUser = async (userIdentifier, userGroups) => {
    await api.post('usergroup/assign/user', {
        target: userIdentifier,
        toAssign: userGroups
    });
};

export const editUserGroupName = async (userGroupName, editedName) => {
    await api.post("usergroup/editname", {
        originalName: userGroupName,
        newName: editedName
    })
}

export const deleteUserGroup = async (userGroupName) => {
    await api.delete(`usergroup/${userGroupName}`);
};

export const changeDefaultUserGroup = async (userGroupName) => {
    await api.post("organisation/usergroup/default", {
        userGroupName: userGroupName,
    })
}

export const unlockUserAccount = async (userIdentifier) => {
    await api.put("userdata/account/unlock", {
        userIdentifier: userIdentifier,
    })
}

export const getOrganisationWorkflows = async () => {
    return (await api.get(`organisationWorkflows`)).data;
}

export const getAssessmentLaunchUrl = async (orderIdentifier) => {
    return (await api.get(`assessment/launchurl/${orderIdentifier}`)).data;
}

export const getAssessmentsForUser = async () => {
    return (await api.get(`userdata/assessment`)).data;
}

export const assignWorkflowToUser = async (workflowIdentifier, emailAddress, dueDate, timeLimitSecs, firstName, lastName) => {
    await api.post(
        "assessment/assign", {
            workflowIdentifier: workflowIdentifier,
            emailAddress: emailAddress,
            dueDate: dueDate,
            timeLimitSecs: timeLimitSecs,
            firstName: firstName,
            lastName: lastName
        });
};

export const editOwnProfile = async (emailAddress, firstName, lastName, mobileNumber) => {
    await api.put('account/edit', {
        firstName: firstName,
        lastName: lastName,
        emailAddress: emailAddress,
        mobileNumber: mobileNumber
    });
};

export const changePassword = async (newPassword, currentPassword) => {
    await api.post("account/password/change", {
        password: newPassword,
        currentPassword: currentPassword
    });
};

export const getHelpAndSupport = async () => {
    return (await api.get(`organisation/helpAndSupport`)).data;
}

export const editHelpAndSupport = async (title, type, content) => {
    await api.put("organisation/helpAndSupport", {
        title: title,
        type: type,
        content: content
    });
}

export const getBranding = async (domainName) => {
    return (await api.get(`organisation/branding/${domainName}`)).data;
}

export const uploadReport = async (email, file, documentName, tags) => {
    var uploadReportForm = new FormData();
    uploadReportForm.append('EmailAddress', email);
    uploadReportForm.append('DocumentName', documentName);
    uploadReportForm.append('Document', file);
    if (tags.length > 0) {
        uploadReportForm.append("Tags", tags);
    }
    return (await api.post("document/upload", uploadReportForm, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }
    )).data;
}

export const editUserGroupTags = async (userGroupName, tags) => {
    await api.post(`usergroup/tags`, {
        target: userGroupName,
        toAssign: tags
    });
}

export const editUserPermissions = async (userIdentifier, permissions) => {
    await api.post(`permission/assign/user`, {
        target: userIdentifier,
        toAssign: permissions
    });
}

export const editUserApplications = async (userIdentifier, applications) => {
    await api.post(`application/assign/user`, {
        target: userIdentifier,
        toAssign: applications
    });
}

export const editUserTags = async (userIdentifier, tags) => {
    await api.post(`userdata/tags`, {
        target: userIdentifier,
        toAssign: tags
    });
}

export const editDocumentTags = async (documentIdentifier, tags) => {
    await api.post(`document/tags`, {
        target: documentIdentifier,
        toAssign: tags
    });
}

export const getApplicationLogo = async (applicationName) => {
    return (await api.get(`application/logo/${applicationName}`)).data;
}

export const setParentUserGroup = async (userGroupName, parentGroupName) => {
    await api.post("usergroup/parent", {
        userGroupName,
        parentGroupName
    });
};

export const requestAzureSSOAuthentication = async (azureLoginCode, state, sessionState) => {
    return await api.post('authentication/login/sso', {
        code: azureLoginCode,
        state: state,
        sessionState: sessionState,
    });
};

export const requestXecurifySSOAuthentication = async (idToken) => {
    return await api.post(`authentication/login/xecurify/${idToken}`);
};

export const getRecommendedJobInfo = async (jobCode) => {
    return (await api.get(`careeringAhead/jobsAndCourses/${jobCode}`)).data;
};